import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUp,
  ChatText,
  Check,
  ClockCounterClockwise,
  NotePencil,
  Receipt,
  UserCircle,
  WarningCircle,
  X,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import {
  ASSISTANT_MIN_MESSAGE_LENGTH,
  type AssistantQuestion,
} from '../../services/invoiceAssistant.service';
import { AssistantIcon } from './AssistantIcon';
import { useInvoiceFlow } from '../hooks/useInvoiceFlow';
import { displayFiscalName } from '../../lib/displayName';
import { formatDraftAmount } from '../lib/draft';
import { SUGGESTIONS_BY_STEP } from './suggestions';
import type { ChatItem } from './types';
import { useInvoiceAssistant } from './useInvoiceAssistant';

export const BetaBadge = () => (
  <span className='bg-h-primary-90 text-h-primary-30 rounded-full px-2 py-0.5 text-[11px] leading-none font-semibold'>
    Beta
  </span>
);

const IconButton = ({
  label,
  onClick,
  pressed,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type='button'
    aria-label={label}
    aria-pressed={pressed}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'text-h-neutral-20 flex size-9 items-center justify-center rounded-lg transition-colors duration-150 disabled:opacity-40',
      pressed && 'bg-h-neutral-94 text-h-text-heading',
    )}>
    {children}
  </button>
);

const ChangeCard = ({
  item,
  canUndo,
  onUndo,
}: {
  item: Extract<ChatItem, { kind: 'change' }>;
  canUndo: boolean;
  onUndo: () => void;
}) => (
  <div className='border-h-neutral-90 rounded-xl border bg-white px-3.5 py-2.5'>
    <div className='flex items-center justify-between gap-2.5'>
      <span className='text-h-text-heading flex items-center gap-2 text-[13px] font-medium'>
        <Check
          size={14}
          weight='bold'
          className={cn('flex-none', item.undone ? 'text-h-neutral-70' : 'text-h-success-50')}
        />
        {item.undone ? 'Cambios deshechos' : 'Actualicé tu borrador'}
      </span>
      {!item.undone && canUndo && (
        <button type='button' onClick={onUndo} className='text-h-primary-50 flex-none text-[13px] font-medium'>
          Deshacer
        </button>
      )}
    </div>
    <ul className='mt-1.5 flex flex-col gap-1 pl-6'>
      {item.lines.map((line) => (
        <li
          key={line.text}
          className={cn(
            'flex flex-wrap items-center gap-1.5 text-[13px] leading-snug',
            item.undone ? 'text-h-text-medium line-through' : 'text-h-neutral-20',
          )}>
          {line.text}
          {line.proposed && !item.undone && (
            <span className='bg-h-warning-90 text-h-warning-30 rounded-full px-1.5 py-px text-[10.5px] font-semibold'>
              Propuesto, confírmalo
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Notice = ({ item }: { item: Extract<ChatItem, { kind: 'notice' }> }) => (
  <div
    className={cn(
      'flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed',
      item.tone === 'taxes' ? 'bg-h-warning-95 text-h-neutral-20' : 'bg-h-primary-95 text-h-neutral-20',
    )}>
    {item.tone === 'taxes' ? (
      <WarningCircle size={15} className='text-h-warning-50 mt-0.5 flex-none' />
    ) : (
      <UserCircle size={15} className='text-h-primary-40 mt-0.5 flex-none' />
    )}
    {item.text}
  </div>
);

/**
 * Una pregunta del último turno. La respuesta viaja como mensaje nuevo con la
 * pregunta adelante: el back solo acepta un número si sale del texto del
 * usuario, y así el mensaje dice a qué se refiere.
 */
const QuestionCard = ({
  question,
  disabled,
  onAnswer,
}: {
  question: AssistantQuestion;
  disabled: boolean;
  onAnswer: (answer: string) => void;
}) => {
  const [value, setValue] = useState('');
  const number = Number(value);
  const outOfRange =
    question.type === 'number' &&
    value !== '' &&
    (!Number.isFinite(number) ||
      (question.min !== undefined && number < question.min) ||
      (question.max !== undefined && number > question.max));

  return (
    <div className='border-h-primary-90 bg-h-primary-95 rounded-xl border px-3.5 py-3'>
      <p className='text-h-text-heading text-[13px] leading-snug font-medium'>
        {question.prompt}
        {question.optional && <span className='text-h-text-medium font-normal'> (opcional)</span>}
      </p>
      {question.type === 'choice' && question.options?.length ? (
        <div className='mt-2 flex flex-wrap gap-1.5'>
          {question.options.map((option) => (
            <button
              key={option.value}
              type='button'
              disabled={disabled}
              onClick={() => onAnswer(option.label)}
              className='border-h-neutral-90 text-h-text-heading min-h-9 rounded-full border bg-white px-3 text-[13px] transition-transform duration-150 active:scale-[0.97] disabled:opacity-40'>
              {option.label}
            </button>
          ))}
        </div>
      ) : (
        <form
          className='mt-2 flex items-center gap-2'
          onSubmit={(event) => {
            event.preventDefault();
            if (!value.trim() || outOfRange) return;
            onAnswer(value.trim());
            setValue('');
          }}>
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            inputMode={question.type === 'number' ? 'decimal' : question.type === 'email' ? 'email' : 'text'}
            type={question.type === 'email' ? 'email' : 'text'}
            aria-label={question.prompt}
            className={cn(
              'text-h-text-heading min-h-9 w-full min-w-0 rounded-lg border bg-white px-3 text-base outline-none md:text-sm',
              outOfRange ? 'border-h-error-50' : 'border-h-neutral-90 focus:border-h-primary-50',
            )}
          />
          <button
            type='submit'
            aria-label='Responder'
            disabled={disabled || !value.trim() || outOfRange}
            className='bg-h-primary-50 flex size-9 flex-none items-center justify-center rounded-full text-white disabled:opacity-40'>
            <ArrowUp size={16} weight='bold' />
          </button>
        </form>
      )}
    </div>
  );
};

const Message = ({ item }: { item: ChatItem }) => {
  if (item.kind === 'user') {
    return (
      <p className='bg-h-primary-95 text-h-text-heading ml-auto max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line'>
        {item.text}
      </p>
    );
  }
  if (item.kind === 'error') {
    return (
      <p className='text-h-error-50 flex items-start gap-2 text-[13px] leading-relaxed'>
        <WarningCircle size={15} className='mt-0.5 flex-none' />
        {item.text}
      </p>
    );
  }
  return (
    <p className='text-h-neutral-20 text-sm leading-relaxed whitespace-pre-line'>
      {item.kind === 'assistant' ? item.text : null}
    </p>
  );
};

const Thinking = () => (
  <p className='text-h-text-medium flex items-center gap-2 text-[13px]' aria-live='polite'>
    <AssistantIcon size={18} />
    Pensando…
  </p>
);

/**
 * El panel del asistente. Es el mismo en escritorio (acoplado a la derecha) y
 * en móvil (hoja de alto completo); lo único que cambia es quién pinta la X:
 * en móvil la pone el `Modal` oficial, así que aquí solo se pinta acoplado.
 */
export const AssistantPanel = ({ docked }: { docked: boolean }) => {
  const assistant = useInvoiceAssistant();
  const { draft, totals } = useInvoiceFlow();
  const [draftText, setDraftText] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const itemCount = assistant?.conversation.items.length ?? 0;
  const isThinking = assistant?.isThinking ?? false;

  // Cada mensaje nuevo baja el scroll al final, como cualquier chat.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [itemCount, isThinking]);

  if (!assistant) return null;
  const { conversation, history, step, send, undo, undoableChangeId, newChat, openConversation } =
    assistant;

  const submit = (text: string) => {
    if (text.trim().length < ASSISTANT_MIN_MESSAGE_LENGTH || isThinking) return;
    send(text);
    setDraftText('');
    setShowHistory(false);
  };

  const contextLabel = draft.receiver
    ? `Factura a ${displayFiscalName(draft.receiver.name)} · ${formatDraftAmount(totals.total)}`
    : 'Factura nueva, sin receptor';

  return (
    <div className='flex h-full min-h-0 flex-col bg-white'>
      <header
        className={cn(
          'border-h-neutral-90 flex min-h-14 flex-none items-center justify-between gap-2 border-b pl-4',
          docked ? 'pr-2' : 'pr-14',
        )}>
        <span className='flex items-center gap-2'>
          <AssistantIcon size={22} />
          <span className='text-h-text-heading text-[15px] font-semibold'>Asistente</span>
          <BetaBadge />
        </span>
        <span className='flex items-center gap-0.5'>
          <IconButton label='Nuevo chat' onClick={newChat} disabled={isThinking || itemCount === 0}>
            <NotePencil size={18} />
          </IconButton>
          <IconButton
            label='Historial'
            pressed={showHistory}
            onClick={() => setShowHistory((open) => !open)}>
            <ClockCounterClockwise size={18} />
          </IconButton>
          {docked && (
            <IconButton label='Cerrar asistente' onClick={assistant.close}>
              <X size={18} />
            </IconButton>
          )}
        </span>
      </header>

      <div ref={scrollRef} className='min-h-0 flex-1 overflow-y-auto px-4 py-4'>
        {showHistory ? (
          <div className='flex flex-col gap-1'>
            <button
              type='button'
              onClick={() => setShowHistory(false)}
              className='text-h-text-heading mb-2 flex min-h-9 items-center gap-1.5 text-[13px] font-medium'>
              <ArrowLeft size={15} />
              Volver al chat
            </button>
            {history.length === 0 ? (
              <p className='text-h-text-medium text-[13px] leading-relaxed'>
                Todavía no hay conversaciones anteriores. Se guardan mientras no cierres la página.
              </p>
            ) : (
              history.map((past) => (
                <button
                  key={past.id}
                  type='button'
                  disabled={isThinking}
                  onClick={() => {
                    openConversation(past.id);
                    setShowHistory(false);
                  }}
                  className='border-h-neutral-90 flex flex-col items-start rounded-xl border bg-white px-3.5 py-2.5 text-left'>
                  <span className='text-h-text-heading w-full truncate text-[13px] font-medium'>
                    {past.title || 'Conversación'}
                  </span>
                  <span className='text-h-text-medium text-xs'>
                    {past.items.length} {past.items.length === 1 ? 'mensaje' : 'mensajes'}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : itemCount === 0 ? (
          <div className='flex min-h-full flex-col items-center justify-center py-8 text-center'>
            <span className='relative mb-5 flex size-16 items-center justify-center'>
              <span className='bg-h-primary-90 absolute inset-0 rounded-full opacity-70 blur-xl' />
              <AssistantIcon size={44} className='relative' />
            </span>
            <h2 className='text-h-text-heading text-lg leading-tight font-semibold tracking-tight md:text-xl'>
              ¿En qué te ayudo con esta factura?
            </h2>
            <p className='text-h-text-medium mt-1.5 max-w-[300px] text-[13px] leading-relaxed'>
              Cuéntame qué cobraste y a quién. Armo el borrador, tú lo revisas y tú timbras.
            </p>
            <div className='mt-5 flex flex-col items-center gap-2'>
              {SUGGESTIONS_BY_STEP[step].map((suggestion) => (
                <button
                  key={suggestion}
                  type='button'
                  onClick={() => submit(suggestion)}
                  className='border-h-neutral-90 text-h-text-heading min-h-9 rounded-full border bg-white px-3.5 py-1.5 text-[13px] transition-transform duration-150 active:scale-[0.97]'>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className='flex flex-col gap-3'>
            {conversation.items.map((item) =>
              item.kind === 'change' ? (
                <ChangeCard
                  key={item.id}
                  item={item}
                  canUndo={item.id === undoableChangeId && !isThinking}
                  onUndo={() => undo(item.id)}
                />
              ) : item.kind === 'notice' ? (
                <Notice key={item.id} item={item} />
              ) : (
                <Message key={item.id} item={item} />
              ),
            )}
            {!isThinking &&
              conversation.questions.map((question) => (
                <QuestionCard
                  key={question.field}
                  question={question}
                  disabled={isThinking}
                  onAnswer={(answer) => send(`${question.prompt} ${answer}`)}
                />
              ))}
            {isThinking && <Thinking />}
          </div>
        )}
      </div>

      <div className='flex-none px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))]'>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit(draftText);
          }}
          className='border-h-neutral-90 focus-within:border-h-primary-50 focus-within:ring-h-primary-50/20 rounded-2xl border bg-white p-2.5 transition-colors duration-150 focus-within:ring-3'>
          <span className='bg-h-neutral-94 text-h-neutral-20 inline-flex max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs'>
            <Receipt size={13} className='text-h-text-medium flex-none' />
            <span className='amp-mask truncate'>{contextLabel}</span>
          </span>
          <div className='mt-1.5 flex items-end gap-2'>
            <textarea
              value={draftText}
              rows={2}
              onChange={(event) => setDraftText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submit(draftText);
                }
              }}
              placeholder='Por ejemplo «Me pagaron $8,000 por una consultoría»'
              aria-label='Mensaje para el asistente'
              className='text-h-text-heading placeholder:text-h-text-light max-h-32 min-h-11 w-full min-w-0 resize-none bg-transparent px-1 text-base leading-snug outline-none md:text-sm'
            />
            <button
              type='submit'
              aria-label='Enviar'
              disabled={draftText.trim().length < ASSISTANT_MIN_MESSAGE_LENGTH || isThinking}
              className='bg-h-primary-50 flex size-9 flex-none items-center justify-center rounded-full text-white transition-transform duration-150 active:scale-[0.94] disabled:opacity-40'>
              <ArrowUp size={17} weight='bold' />
            </button>
          </div>
        </form>
        <div className='text-h-text-medium mt-2 flex items-center justify-between gap-3 px-1 text-[11.5px]'>
          <span>La IA puede equivocarse, revisa antes de timbrar.</span>
          <button type='button' className='flex flex-none items-center gap-1 font-medium'>
            Enviar comentarios
            <ChatText size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};
